import React, { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest, isProduction } from "../services/msalConfig";
import { CheckSSOUser, checkUserExists, fetchCompanyloged } from "../services/apiService";
import toast from "react-hot-toast";
import { InteractionStatus } from "@azure/msal-browser";

const LoginPage = () => {
  const prod = isProduction();
  const { instance, inProgress } = useMsal();
  const triggeredRef = useRef(false);

  // Username/password form state (QA / dev / local only)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [companies, setCompanies] = useState([]);
  const [temp, setTemp] = useState({ companyCode: "" });

  const isSafari = () => {
    const ua = navigator.userAgent;
    return /Safari/.test(ua) && !/Chrome/.test(ua) && !/CriOS/.test(ua) && !/FxiOS/.test(ua) && !/Edg/.test(ua);
  };

  const denyAccess = (deniedEmail) => {
    if (deniedEmail) localStorage.setItem("ssoAccessDeniedEmail", deniedEmail);
    window.location.href = "/unauthorized";
  };

  const handleLoginSuccess = (token) => {
    setError("");
    localStorage.setItem("authToken", token);
    localStorage.setItem("isAuthenticated", "true");
    localStorage.removeItem("ssoAccessDeniedEmail");
    window.location.href = "/";
  };

  // Complete a Microsoft SSO login by validating the account with the backend.
  const finishLogin = async (account) => {
    if (!account?.username) {
      setError("No Microsoft account found.");
      toast.error("No Microsoft account found.");
      return;
    }
    try {
      const response = await CheckSSOUser(account.username);
      if (response && response.token) {
        handleLoginSuccess(response.token);
      } else {
        denyAccess(account.username);
      }
    } catch (err) {
      console.error("CheckSSOUser failed", err);
      denyAccess(account.username);
    }
  };

  // Interactive Microsoft sign-in (popup, with redirect fallback).
  const startLogin = async () => {
    try {
      if (isSafari()) {
        await instance.loginRedirect(loginRequest);
        return;
      }
      try {
        const loginResponse = await instance.loginPopup(loginRequest);
        instance.setActiveAccount(loginResponse.account);
        await finishLogin(loginResponse.account);
      } catch (popupErr) {
        if (popupErr.errorCode === "popup_window_error" || popupErr.errorCode === "empty_window_error") {
          await instance.loginRedirect(loginRequest);
          return;
        }
        throw popupErr;
      }
    } catch (err) {
      console.error("Login failed", err);
      triggeredRef.current = false;
      if (err.errorCode === "user_cancelled") {
        setError("Login cancelled.");
        toast.error("Login cancelled.");
      } else {
        setError("Login failed. Please try again.");
        toast.error("Login failed. Please try again.");
      }
    }
  };

  // Production only: silent SSO first, interactive fallback if not possible.
  const startSilentThenInteractive = async () => {
    try {
      const silent = await instance.ssoSilent(loginRequest);
      instance.setActiveAccount(silent.account);
      await finishLogin(silent.account);
    } catch (silentErr) {
      await startLogin();
    }
  };

  // Production: authenticate without showing the login page.
  useEffect(() => {
    if (!prod) return; // QA/dev/local show the credential form instead
    if (inProgress !== InteractionStatus.None) return;
    if (triggeredRef.current) return;

    if (localStorage.getItem("ssoAccessDeniedEmail")) {
      window.location.href = "/unauthorized";
      return;
    }

    const accounts = instance.getAllAccounts();
    const hasToken = !!localStorage.getItem("authToken");

    if (accounts.length > 0 && hasToken) {
      window.location.href = "/";
      return;
    }
    if (accounts.length > 0 && !hasToken) {
      triggeredRef.current = true;
      finishLogin(accounts[0]);
      return;
    }

    triggeredRef.current = true;
    startSilentThenInteractive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProgress, prod]);

  // QA/dev/local: load the company list for the credential form.
  useEffect(() => {
    if (prod) return;
    const loadCompany = async () => {
      try {
        const data = await fetchCompanyloged();
        setCompanies(data || []);
      } catch (err) {
        console.error("Failed to fetch companies", err);
      }
    };
    loadCompany();
  }, [prod]);

  const handleTempChange = (e) => {
    setTemp((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields.");
      return;
    }
    try {
      const response = await checkUserExists(email, password, temp.companyCode);
      if (response && response.token) {
        handleLoginSuccess(response.token);
      } else {
        setError("Invalid email or password.");
      }
    } catch (err) {
      toast.error("Something went wrong while logging in.");
    }
  };

  // ── Production: keep the login page invisible while silent SSO runs ──────
  if (prod) {
    if (error) {
      return (
        <div className="flex flex-col min-h-screen w-full items-center justify-center bg-white">
          <img src="Recruit-Logo.svg" alt="Company Logo" className="h-12 mb-6" />
          <p className="text-red-600 text-sm mb-4">{error}</p>
          <button
            onClick={() => { setError(""); triggeredRef.current = true; startLogin(); }}
            className="bg-primary-800 text-white px-4 py-2 rounded-md hover:bg-primary-800/70 text-sm"
          >
            Retry Microsoft Sign-in
          </button>
        </div>
      );
    }
    return <div className="min-h-screen w-full bg-white" />;
  }

  // ── QA / dev / local: username & password login form ────────────────────
  return (
    <div className="flex flex-col min-h-screen w-full bg-white relative">
      <div className="relative w-full h-64 sm:h-72 md:h-80 lg:h-96 bg-primary-500">
        <img src="bg1.png" alt="Header Background" className="w-full h-full object-cover" />

        <div className="flex-grow flex items-center justify-center px-4 -mt-16 sm:-mt-20">
          <div className="w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl bg-white p-4 sm:p-6 md:p-8 rounded-2xl filter drop-shadow-[0_5px_30px_rgba(10,60,96,0.10)]">
            <h1 className="text-primary font-semibold text-lg sm:text-xl md:text-2xl lg:text-3xl">
              Sign In
            </h1>
            <p className="text-primary text-xs sm:text-sm md:text-base mt-1 sm:mt-2">
              Sign in using your email or Windows Authentication to access AHC Recruiter services.
            </p>

            <div className="flex justify-center mb-4 sm:mb-6">
              <img src="Recruit-Logo.svg" alt="Company Logo" className="h-10 sm:h-12 md:h-12 object-contain" />
            </div>

            {error && <div className="text-red-600 text-sm mb-2">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              {/* Company */}
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                  Company
                </label>
                <select
                  name="companyCode"
                  value={temp.companyCode}
                  onChange={handleTempChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 sm:py-2.5 focus:outline-none focus:ring-1 focus:ring-primary-800/50 text-sm sm:text-base"
                  required
                >
                  <option value="">Select Company</option>
                  {companies.map((c) => (
                    <option key={c.companyCode} value={c.companyCode}>
                      {c.companyName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 sm:py-2.5 focus:outline-none focus:ring-1 focus:ring-primary-800/50 text-sm sm:text-base"
                  required
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 sm:py-2.5 focus:outline-none focus:ring-1 focus:ring-primary-800/50 text-sm sm:text-base"
                  required
                />
                <div className="text-right mt-1">
                  <a href="/forgot-password" className="text-xs sm:text-sm text-primary-800 underline hover:text-[#6f6246]">
                    Forgot Password?
                  </a>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                className="w-full bg-primary-800 text-white py-2 sm:py-2.5 rounded-md hover:bg-primary-800/70 text-sm sm:text-base transition"
              >
                Login
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-3 sm:my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-xs sm:text-sm text-gray-500">
                <span className="bg-white px-2">or</span>
              </div>
            </div>

            {/* Microsoft Login */}
            <div className="text-center">
              <p className="text-gray-700 text-xs sm:text-sm mb-2">
                Click 'Windows Authentication' to access the portal.
              </p>
              <button onClick={startLogin}>
                <img src="login.svg" alt="Windows Authentication" className="mx-auto h-8 sm:h-10" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
